<template>
  <!-- Structure différente selon que l'utilisateur est authentifié ou non -->
  <div v-if="!isAuthenticated || isOnAuthRoute" class="auth-container">
    <router-view />
  </div>
  
  <!-- Affichage du processus d'onboarding si nécessaire -->
  <!-- Désactivé : la landing page ne s'affiche plus, même dans les paramètres -->
  <!-- <div v-else-if="tenantStore.needsInitialSetup && !isOnSettingsRoute" class="initial-setup-container">
    <OnboardingFlow @setup-complete="onSetupComplete" />
  </div> -->
  
  <!-- Interface principale de l'application -->
  <div v-else class="app-container" :class="{ 'sidebar-open': sidebarOpen }">
    <!-- Sidebar (seulement visible sur les pages authentifiées) -->
    <Sidebar :open="sidebarOpen" @toggle="toggleSidebar" />
    
    <!-- Header (seulement visible sur les pages authentifiées) -->
    <Header :title="currentPageTitle" :class="{ 'sidebar-open': sidebarOpen }" @toggle-sidebar="toggleSidebar" />
    
    <!-- Contenu principal -->
    <main class="main-content with-sidebar">
      <Suspense>
        <template #default>
          <router-view @toggle-sidebar="toggleSidebar" @update-title="updatePageTitle" />
        </template>
        <template #fallback>
          <div class="loading-container">
            <div class="loading-spinner"></div>
            <span class="loading-text">Chargement...</span>
          </div>
        </template>
      </Suspense>
    </main>
    
    <!-- Notifications toast -->
    <ToastNotification />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useTenantStore } from '@/stores/tenant'
import Sidebar from '@/components/Sidebar.vue'
import Header from '@/components/Header.vue'
import ToastNotification from '@/components/ToastNotification.vue'
// import OnboardingFlow from '@/components/OnboardingFlow.vue' // Désactivé : landing page désactivée
import { initializeCardsFromFile } from '@/services/cardNumberService'

const route = useRoute()
const authStore = useAuthStore()
const tenantStore = useTenantStore()

// État initial de la sidebar basé sur la taille d'écran
const getInitialSidebarState = () => {
  const saved = localStorage.getItem('sidebarOpen')
  const isMobile = window.innerWidth < 768
  
  // Sur mobile, toujours fermée par défaut
  if (isMobile) {
    return false
  }
  
  // Sur desktop, utiliser la valeur sauvegardée ou ouverte par défaut
  return saved !== null ? saved === 'true' : false
}

const sidebarOpen = ref(getInitialSidebarState())
const currentPageTitle = ref('Tableau de bord')

// Logique d'authentification simplifiée
const isAuthenticated = computed(() => {
  // Vérifier simplement la présence du token
  return authStore.token !== null && authStore.token.trim() !== ''
})

// Computed séparée pour savoir si on est sur une route d'auth ou publique
const isOnAuthRoute = computed(() => {
  const authRoutes = ['/', '/login', '/register', '/forgot-password']
  // Route publique pour les informations du personnel (accessible via QR code)
  const publicRoutes = ['/personnel/info', '/personnel/qr']
  const isPublicRoute = publicRoutes.some(publicRoute => route.path.startsWith(publicRoute))
  return authRoutes.includes(route.path) || isPublicRoute
})

// Computed pour savoir si on est sur une route de paramètres
const isOnSettingsRoute = computed(() => {
  return route.path.startsWith('/parametres') || route.path.startsWith('/admin/etablissement')
})

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
  localStorage.setItem('sidebarOpen', sidebarOpen.value)
}

const onSetupComplete = () => {
  // Marquer le setup comme terminé pour passer à l'interface principale
  tenantStore.isConfigMissing = false
  tenantStore.isInitialized = true
}

// Fonction pour mettre à jour le titre de la page
const updatePageTitle = (title) => {
  currentPageTitle.value = title
}

// Fonction pour obtenir le titre basé sur la route
const getPageTitleFromRoute = (path) => {
  const titleMap = {
    '/': 'Tableau de bord',
    '/dashboard': 'Tableau de bord',
    '/personnel': 'Personnel',
    '/pointage': 'Pointage',
    '/pointage/calendrier': 'Calendrier des pointages',
    '/directions': 'Directions',
    '/grades': 'Grades',
    '/fonctions': 'Fonctions',
    '/parametres': 'Paramètres',
    '/sites-pointage': 'Sites de pointage',
    '/conges': 'Congés',
    '/admin/etablissement': 'Administration établissement',
    '/change-password': 'Changer mot de passe'
  }
  
  return titleMap[path] || 'OpSolver'
}

// Watcher pour gérer les changements de route et mettre à jour le titre
watch(
  () => route.path,
  (newPath) => {
    // Mettre à jour le titre basé sur la route
    currentPageTitle.value = getPageTitleFromRoute(newPath)
    
    // Si l'utilisateur navigue vers la page de login, s'assurer que l'état est clean
    if (newPath === '/login') {
      // Optionnel : nettoyer certains états si nécessaire
      console.log('Navigation vers la page de login détectée')
    }
  },
  { immediate: true }
)

// Watcher pour gérer les changements d'authentification
watch(
  () => authStore.token,
  (newToken) => {
    // Si le token est supprimé (déconnexion), s'assurer que l'interface se met à jour
    if (!newToken && route.path !== '/login') {
      console.log('Token supprimé, redirection vers login si nécessaire')
    }
  }
)

onMounted(async () => {
  // Initialiser les données des cartes depuis le fichier centralisé
  await initializeCardsFromFile()
  
  // Charger le branding (logo, couleurs) - accessible sans authentification
  try {
    await tenantStore.loadBranding()
  } catch (error) {
    console.log('Aucun branding personnalisé trouvé, utilisation des valeurs par défaut')
  }

  // Charger la configuration complète du tenant seulement si authentifié
  if (isAuthenticated.value) {
    tenantStore.loadConfig()
  }
  
  // Ajouter un listener pour les changements de taille d'écran
  const handleResize = () => {
    const isMobile = window.innerWidth < 768
    // Fermer automatiquement la sidebar sur mobile au redimensionnement
    if (isMobile && sidebarOpen.value) {
      sidebarOpen.value = false
    }
  }
  
  window.addEventListener('resize', handleResize)
  
  // Nettoyer le listener lors de la destruction
  return () => {
    window.removeEventListener('resize', handleResize)
  }
})
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html, body, #app {
  height: 100%;
  width: 100%;
  overflow: hidden;
  font-family: 'Poppins', sans-serif;
  color: #333;
  line-height: 1.6;
  background-color: #f5f5f5;
}

.app-container {
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  transition: all 0.3s ease;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  background-color: #f5f5f5;
  /* Ajout de left: 0 pour les pages sans sidebar comme la connexion */
  left: 0;
  /* Padding-top pour compenser le header fixe */
  padding-top: 64px;
}

.main-content.with-sidebar {
  left: 64px; /* Sidebar fermée */
}

.sidebar-open .main-content.with-sidebar {
  left: 250px; /* Sidebar ouverte */
}

/* Styles pour les écrans mobiles */
@media (max-width: 768px) {
  .main-content {
    /* Padding-top pour mobile (header height: 60px) */
    padding-top: 60px;
  }
  
  .main-content.with-sidebar {
    left: 0;
  }
  
  .sidebar-open .main-content.with-sidebar {
    left: 280px; /* Ajusté pour correspondre à la largeur de la sidebar mobile */
  }
}

/* Style pour le conteneur d'authentification (login/signup) et pages publiques (ex. scan QR) */
.auth-container {
  width: 100%;
  min-height: 100vh;
  height: 100%;
  margin: 0;
  padding: 0;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Style pour le conteneur de configuration initiale */
.initial-setup-container {
  width: 100%;
  height: 100vh;
  margin: 0;
  padding: 0;
  position: relative;
  overflow: auto;
}
</style>
